import * as React from 'react';
import { Component } from 'react';
import { computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import get from 'lodash/get';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { LinkFilter } from '../models/configuration/LinkFilter';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import { fundListFilter } from '../utils/fundListFilter';
import getQueryResults from '../utils/query/getQueryResults';
import normalizeQueryResults from '../utils/query/normalizeQueryResults';
import pivotQueryCollection from '../utils/query/pivotQueryCollection';
import convertRelationToQueryOptions from '../utils/query/convertRelationToQueryOptions';
import DynamicLinkProps from '../models/components/DynamicLinkProps';
import getConditionalData from '../utils/query/getConditionalData';
import getConditionalDataFlag from '../utils/query/getConditionalDataFlag';
import { isNullOrUndefined } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { IQueryKeyValue } from '@kurtosys/ksys-api-client/dist/models/commonTypes';
import { LinkTarget } from '../models/configuration/LinkTarget';

const Link = styled<any, any>('a')`
    ${propStyles};
`;

@inject('dataStore')
@observer
class DynamicLink extends Component<DynamicLinkProps, any> {
    static defaultProps = {
        color: '#212529',
    };

    @computed
    get link(): string | LinkFilter {
        const { config, shareClass, dataStore } = this.props;
        if (config && shareClass && dataStore) {
            const { linkProperty, url } = config;
            let urlTemplate: string | LinkFilter;
            // Try get url template from a specific property
            if (linkProperty) {
                urlTemplate = get(
                    shareClass,
                    `properties_pub.${linkProperty}.value`,
                    ''
                );
            }

            // Fallback to get url template using the lookup to the links config
            if (!urlTemplate && dataStore.config.selectedModule.config.links) {
                let reference;
                if (typeof url === 'string') {
                    reference = url;
                } else if (url && url.url) {
                    reference = url.url;
                }
                if (reference) {
                    urlTemplate =
                        dataStore.config.selectedModule.config.links[reference];
                }
            }

            // Fallback to use the url template straight from the config url property
            if (!urlTemplate) {
                urlTemplate = url;
            }
            return urlTemplate;
        }
        return;
    }

    @computed
    get linkUrl(): string {
        const { config, shareClass } = this.props;
        if (config && shareClass) {
            const link: string | LinkFilter = this.link;

            // if object is LinkFilter then get url from LinkFilter else use it as is
            if (link) {
                let url = '';
                if (typeof link === 'string') {
                    url = link;
                } else if (link.url) {
                    url = link.url;
                }

                const prefixes = ['http://', 'https://'];
                if (
                    !prefixes.find(prefix => url.startsWith(prefix)) &&
                    typeof link !== 'string' &&
                    link.preserveOrigin
                ) {
                    const origin = `${window.location.origin}${window.location.pathname}`;
                    if (url.startsWith('/') && origin.endsWith('/')) {
                        url = url.substr(1);
                    }
                    const insertSlash =
                        !url.startsWith('/') && !origin.endsWith('/');
                    url = `${origin}${insertSlash ? '/' : ''}${url}`;
                }

                return url;
            }

            return;
        }
        return;
    }

    @computed
    get linkQueries(): IQueryKeyValue {
        const { config, shareClass } = this.props;
        if (config && shareClass) {
            const linkFilter = this.link as LinkFilter;

            if (linkFilter && linkFilter.queries) {
                const queryOptions = convertRelationToQueryOptions(
                    linkFilter.queries
                );
                return pivotQueryCollection(queryOptions);
            }

            return;
        }
        return;
    }

    @computed
    get linkQueryResults() {
        const { dataStore, queryContext } = this.props;
        return getQueryResults(
            queryContext,
            this.linkQueries,
            null,
            dataStore.appDataProps
        );
    }

    @computed
    get checkEnableByFundList() {
        const { shareClass, dataStore } = this.props;
        const link = this.link as LinkFilter;
        if (!isNullOrUndefined(link)) {
            const { fundListCheck } = link;
            if (fundListCheck) {
                return dataStore.fundListContainsEntity(
                    shareClass as any,
                    fundListCheck
                );
            }
        }
        return true;
    }

    @computed
    get linkDisabled() {
        const { config, shareClass } = this.props;
        if (config && shareClass) {
            const link = this.link as LinkFilter;
            if (!this.checkEnableByFundList) {
                return true;
            }
            if (link && link.disabledFlag) {
                const data = normalizeQueryResults({
                    ...this.props.shareClass.properties_pub,
                    ...getConditionalData(shareClass, link.disabledFlag),
                });
                return getConditionalDataFlag(data, link.disabledFlag);
            }
        }
        return false;
    }

    @computed
    get target(): LinkTarget | undefined {
        const link: string | LinkFilter = this.link;
        if (link) {
            if (typeof link === 'string') {
                return '_self';
            }
            if (link.target) {
                return link.target;
            }
        }

        return;
    }

    @computed
    get href() {
        const data = normalizeQueryResults({
            ...this.props.shareClass.properties_pub,
            ...this.linkQueryResults,
        });
        const parsedLink = replacePlaceholders({
            values: data,
            text: this.linkUrl,
            appParams: this.props.dataStore.appDataProps,
        });

        return parsedLink;
    }

    @computed
    get canDisplayLink() {
        const { dataStore, shareClass, config, showLink = true } = this.props;
        return (
            showLink &&
            !this.linkDisabled &&
            fundListFilter(config as any, dataStore, shareClass)
        );
    }

    render() {
        const {
            children,
            showLink,
            config,
            dataStore,
            shareClass,
            ...aLinkProps
        } = this.props;

        if (this.canDisplayLink) {
            return (
                <Link
                    href={this.href}
                    target={this.target}
                    onClick={e => {
                        e.stopPropagation();
                    }}
                    {...aLinkProps}
                >
                    {children}
                </Link>
            );
        }

        return children;
    }
}

export default withTheme(DynamicLink) as any;



// WEBPACK FOOTER //
// ./src/Components/DynamicLink.tsx